import React, { useState, useEffect, useRef } from 'react';
import { Send, PhoneAndroid, AccountBalance } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
    fetchProviderTypes,
    fetchWithdrawalProviders,
    createWithdrawalRequest,
} from '../../services/withdrawalService';
import AmountInput from '../common/AmountInput';
import { parseAmount, normalizeAmountForApi } from '../../utils/amount';

const generateId = () =>
    Date.now().toString(36) + Math.random().toString(36).substr(2);

const inputClass =
    'w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 hover:border-slate-300';

const labelClass = 'block text-sm font-semibold text-slate-800 mb-2';

const WithdrawalRequestForm = ({ onSuccess, onCancel }) => {
    const { selectedOrganization, user } = useAuth();
    const [amount, setAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [loadingProviders, setLoadingProviders] = useState(false);
    const [loadingProviderTypes, setLoadingProviderTypes] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState({});
    const [providers, setProviders] = useState([]);
    const [providerTypes, setProviderTypes] = useState([]);
    const [accountFormatError, setAccountFormatError] = useState(false);
    const accountRef = useRef(null);
    const previousValue = useRef('');

    const initModel = () => ({
        accountable: user?.id,
        use_extra_fees: false,
        extra_fees: 0,
        service: null,
        currency: 'CDF',
        sender_id: selectedOrganization?.id,
        state: '',
        ref: generateId(),
        label: 'Demande de retrait',
        provider_type: null,
        method: {
            prefix: '243',
            provider_id: null,
            suffix: null,
            account: null,
            full_name: null,
            expiry_date: null,
        },
    });

    const [model, setModel] = useState(initModel);

    const amountNum = parseAmount(amount);
    const isAmountValid = Number.isFinite(amountNum) && amountNum > 0;
    const canSubmit =
        isAmountValid &&
        model.method.provider_id &&
        model.method.account &&
        !accountFormatError &&
        !submitting;

    useEffect(() => {
        loadProviderTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (model.provider_type !== null) {
            loadProviders();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model.provider_type]);

    useEffect(() => {
        if (selectedProvider?.provider_type?.toUpperCase() === 'MOBILE MONEY') {
            setModel((prev) => ({ ...prev, method: { ...prev.method, prefix: '243' } }));
            setSelectedProvider((prev) => ({ ...prev, with_prefix: true }));
        } else {
            setModel((prev) => ({ ...prev, method: { ...prev.method, prefix: null } }));
        }
        if (selectedProvider?.id) {
            setModel((prev) => ({
                ...prev,
                method: { ...prev.method, account: '', provider_id: selectedProvider.id },
            }));
        }
    }, [selectedProvider?.id]);

    useEffect(() => {
        if (
            model.method.account?.length === selectedProvider.account_length ||
            (selectedProvider.account_format &&
                model.method.account?.match(selectedProvider.account_format))
        ) {
            accountRef.current?.blur();
        }
        if (
            model.method.account?.length === selectedProvider.account_length &&
            selectedProvider.account_format &&
            !model.method.account.match(selectedProvider.account_format)
        ) {
            setAccountFormatError(true);
        } else {
            setAccountFormatError(false);
        }
    }, [model.method?.account, selectedProvider]);

    const loadProviders = async () => {
        try {
            setLoadingProviders(true);
            const response = await fetchWithdrawalProviders({
                pge: 1,
                typ: model.provider_type,
                opr: 'WDL',
                lmt: 100,
                slug: '',
            });

            if (response?.length > 0) {
                setProviders(response);
                setSelectedProvider(response[0]);
            } else {
                setProviders([]);
                setSelectedProvider({});
            }
        } catch (error) {
            console.error('Error loading providers:', error);
        } finally {
            setLoadingProviders(false);
        }
    };

    const loadProviderTypes = async () => {
        try {
            setLoadingProviderTypes(true);
            const response = await fetchProviderTypes();

            if (response?.length > 0) {
                setProviderTypes(response);
                setModel((prev) => ({ ...prev, provider_type: response[0] }));
            }
        } catch (error) {
            console.error('Error loading provider types:', error);
        } finally {
            setLoadingProviderTypes(false);
        }
    };

    const handleChange = (text) => {
        if (selectedProvider.provider_type?.toLowerCase() === 'netikash') {
            const isDeleting = text.length < previousValue.current.length;
            if (!isDeleting) {
                text = formatAccountNumber(text);
            }
            previousValue.current = text;
        }
        setModel((prev) => ({ ...prev, method: { ...prev.method, account: text } }));
    };

    const handleSelectProvider = (provider_id) => {
        const selected = providers.find((provider) => provider.id === provider_id);
        if (selected) {
            setSelectedProvider(selected);
        }
    };

    const formatAccountNumber = (input) => {
        const raw = input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        let result = '';

        if (raw.length >= 2) {
            result += raw.substring(0, 2) + '-';
        } else {
            return raw;
        }

        if (raw.length >= 6) {
            result += raw.substring(2, 6) + '-';
        } else {
            result += raw.substring(2);
            return result;
        }

        result += raw.substring(6, 9);
        return result;
    };

    const handleSubmit = async () => {
        const normalized = normalizeAmountForApi(amount, model.currency);
        if (normalized == null || normalized <= 0) {
            alert('Le montant est vide ou inférieur à zéro');
            return;
        }

        try {
            setSubmitting(true);
            const solution_id = localStorage.getItem('netikash_solution');

            const withdrawalData = {
                ...model,
                amount: normalized,
                solution_id,
            };

            const response = await createWithdrawalRequest(withdrawalData);

            if (response) {
                const { error, message, expires_in, trans_id } = response;
                if (error) {
                    alert(message || 'Erreur lors de la création de la demande');
                    return;
                }

                onSuccess({ expires_in, trans_id, message });
            }
        } catch (error) {
            console.error('Error creating withdrawal request:', error);
            alert(error.message || 'Erreur lors de la création de la demande de retrait');
        } finally {
            setSubmitting(false);
        }
    };

    const providerIcon =
        model.provider_type?.toLowerCase().includes('mobile') ||
        model.provider_type?.toLowerCase().includes('money') ? (
            <PhoneAndroid className="text-amber-600" />
        ) : (
            <AccountBalance className="text-amber-600" />
        );

    return (
        <div className="space-y-6">
            {/* Montant */}
            <section className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50/80 to-orange-50/40 p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <label className={labelClass}>Montant à retirer</label>
                    <button
                        type="button"
                        onClick={() => {
                            setModel((prev) => ({
                                ...prev,
                                currency: prev.currency === 'CDF' ? 'USD' : 'CDF',
                            }));
                            setAmount('');
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold tracking-wide bg-white border border-amber-200 text-amber-800 hover:bg-amber-50 hover:border-amber-300 transition-all duration-200 shadow-sm"
                        aria-label="Changer la devise"
                    >
                        {model.currency === 'CDF' ? 'USD' : 'CDF'}
                    </button>
                </div>
                <div className="flex gap-2">
                    <AmountInput
                        value={amount}
                        onChange={setAmount}
                        currency={model.currency}
                        className={`flex-1 ${inputClass} text-lg font-semibold tabular-nums`}
                        placeholder={model.currency === 'CDF' ? 'Ex: 50000' : 'Ex: 150.00'}
                        aria-label="Montant du retrait"
                    />
                    <div className="flex items-center justify-center min-w-[72px] px-3 py-3 rounded-xl bg-white border border-amber-200 text-sm font-bold text-amber-900 shadow-sm">
                        {model.currency}
                    </div>
                </div>
                <p className="mt-2 text-xs text-amber-800/70">
                    Saisissez le montant net que vous souhaitez recevoir sur votre compte.
                </p>
            </section>

            {/* Méthode */}
            <section className="space-y-4">
                <div className="flex items-center gap-2 text-slate-800">
                    {providerIcon}
                    <h4 className="text-sm font-semibold">Méthode de retrait</h4>
                </div>

                <div>
                    <label className={labelClass}>Type de canal</label>
                    {loadingProviderTypes ? (
                        <div className="flex justify-center py-6">
                            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <select
                            value={model.provider_type || ''}
                            onChange={(e) =>
                                setModel((prev) => ({ ...prev, provider_type: e.target.value }))
                            }
                            className={inputClass}
                        >
                            {providerTypes.map((type, index) => (
                                <option key={`provider-type-${index}`} value={type}>
                                    {type?.toUpperCase()}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {model.provider_type !== null && (
                    <>
                        <div>
                            <label className={labelClass}>Opérateur / banque</label>
                            {loadingProviders ? (
                                <div className="flex justify-center py-6">
                                    <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : (
                                <select
                                    value={model.method.provider_id || ''}
                                    onChange={(e) => handleSelectProvider(e.target.value)}
                                    className={inputClass}
                                >
                                    {providers.map((provider, index) => (
                                        <option key={`provider-${index}`} value={provider.id}>
                                            {provider.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {selectedProvider?.account_label && (
                            <div>
                                <label className={labelClass}>{selectedProvider.account_label}</label>
                                <div className="flex gap-2">
                                    {selectedProvider.with_prefix && (
                                        <input
                                            type="text"
                                            readOnly
                                            value={model.method.prefix}
                                            className="w-[72px] shrink-0 px-3 py-3 rounded-xl text-center text-sm font-semibold bg-slate-50 border border-slate-200 text-slate-600"
                                            aria-label="Indicatif pays"
                                        />
                                    )}
                                    <input
                                        ref={accountRef}
                                        type={
                                            selectedProvider?.provider_type?.toLowerCase() ===
                                            'netikash'
                                                ? 'text'
                                                : 'tel'
                                        }
                                        autoCapitalize={
                                            selectedProvider?.provider_type?.toLowerCase() ===
                                            'netikash'
                                                ? 'characters'
                                                : 'none'
                                        }
                                        autoCorrect="off"
                                        value={model.method.account || ''}
                                        onChange={(e) => handleChange(e.target.value)}
                                        placeholder={selectedProvider?.account_placeholder}
                                        maxLength={selectedProvider.account_length}
                                        className={`flex-1 ${inputClass} ${
                                            accountFormatError
                                                ? 'border-red-400 focus:border-red-500 focus:ring-red-500/30'
                                                : ''
                                        }`}
                                    />
                                </div>
                                {accountFormatError && (
                                    <p className="mt-2 text-sm text-red-600 font-medium">
                                        Format de compte invalide
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2 border-t border-slate-100">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    className="flex-1 px-6 py-3 rounded-xl font-semibold text-slate-700 bg-slate-100 border border-slate-200 hover:bg-slate-200 transition-all duration-200 disabled:opacity-50"
                >
                    Annuler
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className="flex-1 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md shadow-amber-500/25 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
                >
                    {submitting ? (
                        <>
                            <span className="w-5 h-5 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
                            <span>Envoi en cours…</span>
                        </>
                    ) : (
                        <>
                            <Send className="text-lg" />
                            <span>Envoyer</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default WithdrawalRequestForm;
